import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../components/fields/input";
import Button from "../../../../components/buttons/button";
import {
  useAddDisplayMutation,
  useUpdateDisplayMutation,
} from "../../../../store/Reducer/display";
import toast from "react-hot-toast";

export default function AddDisplayModal({
  isOpen,
  closeModal,
  isEdit,
  rowData,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [layout, setLayout] = useState("horizontal");
  const [isActive, setIsActive] = useState(true);

  const [addDisplay, { isLoading }] = useAddDisplayMutation();
  const [updateDisplay, { isLoading: updateDisplayLoading }] =
    useUpdateDisplayMutation();

  console.log("isedit", isEdit);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let displayData = {
        deviceName: name,
        screenOrientation: layout,
      };

      // Only include OTP if it's a new entry (not in edit mode)
      if (!isEdit) {
        displayData.otp = otp;
        
      }
      if(isEdit)
      {
        displayData.isActive = isActive
      }

      if (isEdit) {
        await updateDisplay({ id: rowData?._id, data: displayData }).unwrap();
        toast.success("Display updated successfully!");
      } else {
        await addDisplay(displayData).unwrap();
        toast.success("Display added successfully!");
      }

      closeModal(); // Close the modal after successful submission
    } catch (error) {
      console.log("Error submitting form:", error);
      toast.error(error?.data?.message || "An error occurred!");
    }
  };

  // Update form data when modal opens for editing
  useEffect(() => {
    if (isOpen && isEdit && rowData) {
      setName(rowData?.deviceName || "");
      setLayout(rowData?.screenOrientation || "horizontal");
      setOtp(rowData?.otp || "");
    }
  }, [isOpen, isEdit, rowData]);

  // Reset form when modal closes

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setLayout("horizontal");
      setOtp(""); // Ensure OTP resets when modal is closed
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 overflow-y-auto bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 sm:p-0"
          onClick={closeModal}
        >
          <div
            className="rounded-lg shadow-xl w-full max-w-lg mx-auto h-full sm:h-auto sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-full bg-white rounded-lg shadow-lg p-4 sm:px-6 sm:py-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                  {isEdit ? "Update Display" : "Add Display"}
                </h1>

                {(isLoading || updateDisplayLoading) && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="loader border-t-4 border-blue-500 rounded-full w-16 h-16 animate-spin"></div>
                    <p className="text-white mt-4">Loading...</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    label="Enter Name"
                    type="text"
                    name="name"
                    placeholder="Enter Name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />

                  {/* Show OTP field only when adding a new display */}
                  {!isEdit && (
                    <div>
                      <Input
                        label="Code shown on the screen"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        name="otp"
                        placeholder="Code shown on the screen"
                        required
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\s/g, ""))}
                      />
                      <p className="mt-1 text-xs text-gray-500">Open the Masjid Hub display app on the screen; it shows a code to type here.</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-700">
                      {t("LayoutText")}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setLayout("horizontal")}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          layout === "horizontal"
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-200"
                        }`}
                      >
                        <div className="w-full h-8 bg-gray-200 rounded mb-2"></div>
                        <span className="text-sm font-medium">
                          {t("HorizontalText")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLayout("vertical")}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          layout === "vertical"
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-200"
                        }`}
                      >
                        <div className="w-8 h-16 bg-gray-200 rounded mb-2 mx-auto"></div>
                        <span className="text-sm font-medium">
                          {t("VerticalText")}
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-center sm:justify-end items-center flex-wrap space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-colors duration-200 ease-in-out focus:outline-none flex items-center text-gray-800 border border-gray-700 hover:bg-gray-100 justify-center"
                    >
                      {t("universal_cancel_text")}
                    </button>

                    <Button
                      text={t("universal_save_text")}
                      type="submit"
                      className="px-6 text-nowrap"
                      disabled={isLoading || updateDisplayLoading}
                    />
                    {isEdit && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActive"
                          id="isActive"
                          checked={isActive}
                          onChange={(e) => {
                            console.log(
                              "Checkbox clicked, new value:",
                              e.target.checked
                            ); // Debugging
                            setIsActive(e.target.checked);
                          }}
                        />
                        <label
                          htmlFor="isActive"
                          className="ml-2 text-sm text-gray-700"
                        >
                          Active
                        </label>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
